import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { timeAgo, STATUSES, PRIORITIES, CATEGORIES } from '../../utils/helpers';
import { exportToExcel } from '../../utils/exportExcel';

export default function CustomerManagerTickets() {
  const [data, setData] = useState({ tickets: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ status: '', priority: '', category: '', search: '', page: 1 });

  useEffect(() => {
    setLoading(true);
    setFetchError('');
    const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
    apiFetch(`/tickets?${params}&limit=25`)
      .then(setData)
      .catch(err => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, [filters]);

  function setFilter(key, value) {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && k !== 'page'))
      );
      const all = await apiFetch(`/tickets?${params}&limit=5000`);
      exportToExcel(all.tickets || [], [
        { header: 'Ticket #',     key: 'ticket_number' },
        { header: 'Title',        key: 'title' },
        { header: 'Raised By',    key: 'customer_name' },
        { header: 'Product',      key: 'product_name' },
        { header: 'Serial No.',   key: 'serial_number' },
        { header: 'Category',     key: 'category' },
        { header: 'Priority',     key: 'priority' },
        { header: 'Status',       key: 'status' },
        { header: 'Assigned To',  key: 'agent_name' },
        { header: 'SLA Breached', key: 'sla_breached' },
        { header: 'Due Date',     key: 'due_at' },
        { header: 'Close Reason', key: 'close_reason' },
        { header: 'Close Remark', key: 'close_remark' },
        { header: 'Created',      key: 'created_at' },
        { header: 'Updated',      key: 'updated_at' },
        { header: 'Closed At',    key: 'closed_at' },
      ], `company-tickets-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Company's Tickets</h1>
          <p className="page-sub">{data.total} ticket{data.total !== 1 ? 's' : ''} across your organisation</p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={handleExport}
          disabled={exporting || data.total === 0}
          style={{ fontSize: 13 }}
        >
          {exporting ? '⏳ Exporting…' : '📊 Export to Excel'}
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <input
          className="input"
          style={{ maxWidth: 240, flex: '1 1 180px' }}
          placeholder="🔍  Search tickets…"
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
        />
        <select className="input" style={{ maxWidth: 150 }} value={filters.status} onChange={e => setFilter('status', e.target.value)}>
          <option value="">All Status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select className="input" style={{ maxWidth: 150 }} value={filters.priority} onChange={e => setFilter('priority', e.target.value)}>
          <option value="">All Priority</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="input" style={{ maxWidth: 160 }} value={filters.category} onChange={e => setFilter('category', e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('-', ' ')}</option>)}
        </select>
      </div>

      {fetchError && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          Error loading tickets: {fetchError}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>
      ) : (
        <div className="table-wrap">
          <table className="kg-table">
            <thead>
              <tr>
                {['Ticket #', 'Title', 'Raised By', 'Product', 'Priority', 'Status', 'SLA / Due', 'Updated'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.tickets.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state-icon">🎫</div>
                      <div className="empty-state-title">No tickets found</div>
                      <div className="empty-state-sub">Try adjusting your filters</div>
                    </div>
                  </td>
                </tr>
              ) : data.tickets.map(t => {
                const breached = t.sla_breached;
                return (
                  <tr key={t.id} className={breached ? 'sla-breached' : ''}>
                    <td>
                      <Link to={`/customer-manager/tickets/${t.id}`}>
                        <span className="ticket-chip">{t.ticket_number}</span>
                      </Link>
                    </td>
                    <td style={{ maxWidth: 240 }}>
                      <Link
                        to={`/customer-manager/tickets/${t.id}`}
                        style={{ fontWeight: 600, color: 'var(--text-main)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {t.title}
                      </Link>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{t.customer_name}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{t.product_name || '—'}</td>
                    <td><span className={`badge badge-${t.priority}`}>{t.priority}</span></td>
                    <td><span className={`badge badge-${t.status}`}>{t.status.replace('_', ' ')}</span></td>
                    <td style={{ fontSize: 12.5, color: breached ? '#b91c1c' : 'var(--text-secondary)', fontWeight: breached ? 700 : 400 }}>
                      {breached ? '⚠ Breached' : (t.due_at ? new Date(t.due_at).toLocaleDateString('en-IN') : '—')}
                    </td>
                    <td style={{ color: '#94a3b8', fontSize: 12 }}>{timeAgo(t.updated_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data.total > 25 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button className="btn btn-secondary btn-sm" disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>← Prev</button>
          <span style={{ padding: '5px 10px', fontSize: 13 }}>Page {filters.page}</span>
          <button className="btn btn-secondary btn-sm" disabled={filters.page * 25 >= data.total} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next →</button>
        </div>
      )}
    </div>
  );
}
